import { z } from "zod";

// Summary of all compute pools
export const computePoolSummaryItemSchema = z
  .object({
    compute_pool_name: z.string(),
    total_cost: z.number().or(z.string().transform(Number)),
    total_runs: z
      .number()
      .optional()
      .nullable()
      .transform(val => (val ?? undefined) as number | undefined),
    owner: z.string().optional().nullable(),
    is_exclusive: z.boolean().optional().nullable(),
    application_name: z.string().optional().nullable(),
  })
  .passthrough();

export type ComputePoolSummaryItem = z.infer<typeof computePoolSummaryItemSchema>;

export const computePoolDailyCostItemSchema = z
  .object({
    date: z.string(),
    cost: z.number().or(z.string().transform(Number)).optional(),
    credits_used: z.number().or(z.string().transform(Number)).optional(),
  })
  .passthrough();

export type ComputePoolDailyCostItem = z.infer<typeof computePoolDailyCostItemSchema>;

export const computePoolMetadataItemSchema = z
  .object({
    name: z.string(),
    is_suspended: z.boolean().optional().nullable(),
    min_nodes: z.number().or(z.string().transform(Number)).optional().nullable(),
    max_nodes: z.number().or(z.string().transform(Number)).optional().nullable(),
    instance_family: z.string().optional().nullable(),
    auto_suspend_secs: z.number().or(z.string().transform(Number)).optional().nullable(),
    auto_resume: z.boolean().optional().nullable(),
    created: z.string().optional().nullable(),
    last_resumed: z.string().optional().nullable(),
    last_altered: z.string().optional().nullable(),
    deleted: z.string().optional().nullable(),
    owner: z.string().optional().nullable(),
    owner_role_type: z.string().optional().nullable(),
    is_exclusive: z.boolean().optional().nullable(),
    application_name: z.string().optional().nullable(),
    application_id: z.number().or(z.string().transform(Number)).optional().nullable(),
    comment: z.string().optional().nullable(),
    total_cost: z.number().or(z.string().transform(Number)).optional().nullable(),
    snowflake_tags: z.array(z.string()).optional().nullable(),
  })
  .passthrough();

export type ComputePoolMetadataItem = z.infer<typeof computePoolMetadataItemSchema>;

export const computePoolOverviewSchema = z
  .object({
    compute_pool_name: z.string(),
    total_cost: z.number().or(z.string().transform(Number)),
    total_runs: z.number().or(z.string().transform(Number)).optional().nullable(),
    // New backend shape nests metadata
    metadata: computePoolMetadataItemSchema.optional(),
  })
  .passthrough();

export type ComputePoolOverview = z.infer<typeof computePoolOverviewSchema>;

export const computePoolEntrySchema = z
  .object({
    compute_pool_name: z.string().optional().nullable(),
    start_time: z.string(),
    end_time: z.string().optional().nullable(),
    credits_used: z.number().or(z.string().transform(Number)),
    application_name: z.string().optional().nullable(),
    application_id: z.union([z.string(), z.number()]).optional().nullable(),
    is_exclusive: z.boolean().optional().nullable(),
    instance_id: z.string().optional().nullable(),
  })
  .passthrough();

export type ComputePoolEntry = z.infer<typeof computePoolEntrySchema>;

export const computePoolsGraphDataSchema = z.array(
  z.object({
    date: z.string(),
    pool_spends: z.record(z.number().or(z.string().transform(Number))),
  })
);

export type ComputePoolsGraphDataItem = z.infer<typeof computePoolsGraphDataSchema>[number];

// Aggregated summary for all compute pools
export const computePoolsSummarySchema = z.object({
  total_spend: z.number().or(z.string().transform(Number)),
  average_daily_spend: z.number().or(z.string().transform(Number)),
  active_pools: z.number().or(z.string().transform(Number)),
  exclusive_pools: z.number().or(z.string().transform(Number)),
});

export type ComputePoolsSummary = z.infer<typeof computePoolsSummarySchema>;

// ── Utilization (SPCS node telemetry) ──
//
// `.nullable()` on every ratio is load-bearing, not defensive. The SPCS
// metrics agent writes an absent gauge as 0 rather than omitting it, and the
// backend is careful to turn that back into null; a `.default(0)` or a
// `?? 0` anywhere downstream would undo that and present telemetry nobody
// collected as a measured zero. Read `has_cpu_telemetry` / `has_mem_telemetry`
// before rendering a metric, never the truthiness of a value.
const nullableRatio = z.number().or(z.string().transform(Number)).nullable().optional();

export const computePoolUtilizationPointSchema = z
  .object({
    bucket: z.string(),
    node_count: z.number(),
    sample_count: z.number(),
    cpu_sample_count: z.number(),
    mem_sample_count: z.number(),

    // Pool-level absolutes: cores and bytes summed across the bucket's nodes.
    // These are what the chart plots — a pool autoscales, so a ratio's
    // denominator moves between buckets and cannot be read across time.
    cpu_capacity_cores: nullableRatio,
    mem_capacity_bytes: nullableRatio,
    cpu_used_avg: nullableRatio,
    mem_used_avg: nullableRatio,
    cpu_used_p95: nullableRatio,
    mem_used_p95: nullableRatio,
    // Upper bound: the sum of each node's peak, which did not necessarily
    // happen in the same instant.
    cpu_used_max: nullableRatio,
    mem_used_max: nullableRatio,

    // Ratios, for the "% of capacity" the tooltip shows alongside.
    cpu_util_avg: nullableRatio,
    cpu_util_p95: nullableRatio,
    cpu_util_max: nullableRatio,
    mem_util_avg: nullableRatio,
    mem_util_p95: nullableRatio,
    mem_util_max: nullableRatio,

    cpu_quiet_node_hours: z.number().optional(),
  })
  .passthrough();

export type ComputePoolUtilizationPoint = z.infer<typeof computePoolUtilizationPointSchema>;

export const computePoolUtilizationCoverageSchema = z
  .object({
    has_cpu_telemetry: z.boolean(),
    has_mem_telemetry: z.boolean(),
    node_count: z.number(),
    node_hours: z.number(),
    measured_hours: z.number(),
    sample_count: z.number(),
    cpu_sample_count: z.number(),
    mem_sample_count: z.number(),
    peak_cpu_util: nullableRatio,
    peak_mem_util: nullableRatio,
    cpu_quiet_node_hours: z.number(),
  })
  .passthrough();

export type ComputePoolUtilizationCoverage = z.infer<typeof computePoolUtilizationCoverageSchema>;

export const computePoolUtilizationSchema = z.object({
  compute_pool_name: z.string(),
  aggregation_level: z.string(),
  coverage: computePoolUtilizationCoverageSchema,
  data: z.array(computePoolUtilizationPointSchema),
});

export type ComputePoolUtilization = z.infer<typeof computePoolUtilizationSchema>;
